import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus,
    ArrowRight,
    ExternalLink,
    Target,
    Users,
    ShoppingCart,
    DollarSign,
    MoreVertical,
    Edit2,
    Trash2
} from 'lucide-react';
import { FUNNEL_STAGES, formatBudget } from '../../utils/funnelHelpers';
import ConfirmationModal from '../ConfirmationModal';

/**
 * QuickFunnelBuilder Component
 * Simplified horizontal funnel view for Command Center
 * Shows TOFU → MOFU → BOFU stages with campaign cards
 */
const QuickFunnelBuilder = ({ campaigns = [], onAddCampaign, onEditCampaign, onDeleteCampaign, activePlatform }) => {
    const navigate = useNavigate();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);
    const [openMenuId, setOpenMenuId] = useState(null);

    // Group campaigns by funnel stage
    const groupedCampaigns = {
        TOFU: campaigns.filter(c => c.funnelStage === 'TOFU'),
        MOFU: campaigns.filter(c => c.funnelStage === 'MOFU'),
        BOFU: campaigns.filter(c => c.funnelStage === 'BOFU')
    };

    // Calculate total budget by stage
    const stageBudgets = {
        TOFU: groupedCampaigns.TOFU.reduce((sum, c) => sum + (c.budget?.amount || 0), 0),
        MOFU: groupedCampaigns.MOFU.reduce((sum, c) => sum + (c.budget?.amount || 0), 0),
        BOFU: groupedCampaigns.BOFU.reduce((sum, c) => sum + (c.budget?.amount || 0), 0)
    };

    const totalBudget = Object.values(stageBudgets).reduce((a, b) => a + b, 0);

    const stageIcons = {
        TOFU: Target,
        MOFU: Users,
        BOFU: ShoppingCart
    };

    const stageColors = {
        TOFU: 'var(--color-info)',
        MOFU: 'var(--color-warning)',
        BOFU: 'var(--color-success)'
    };

    const handleDeleteClick = (campaign) => {
        setCampaignToDelete(campaign);
        setShowDeleteConfirm(true);
        setOpenMenuId(null);
    };

    const handleConfirmDelete = () => {
        if (campaignToDelete && onDeleteCampaign) {
            onDeleteCampaign(campaignToDelete.id);
        }
        setShowDeleteConfirm(false);
        setCampaignToDelete(null);
    };

    const handleMenuToggle = (campaignId, e) => {
        e.stopPropagation();
        setOpenMenuId(openMenuId === campaignId ? null : campaignId);
    };

    return (
        <div className="card" style={{ padding: 'var(--space-4)' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-4)'
            }}>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 600
                    }}>
                        Quick Funnel Builder
                    </h3>
                    <p style={{
                        margin: 'var(--space-1) 0 0',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        Plan your {activePlatform?.toUpperCase() || 'ad'} campaign funnel
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)'
                    }}>
                        <DollarSign size={14} style={{ color: 'var(--color-primary)' }} />
                        <span style={{ color: 'var(--color-text-secondary)' }}>Total:</span>
                        <span style={{ fontWeight: 600 }}>{formatBudget(totalBudget)}</span>
                    </div>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate('/funnel-builder')}
                    >
                        <ExternalLink size={14} />
                        Open Full Builder
                    </button>
                </div>
            </div>

            {/* Funnel Stages */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr auto 1fr',
                gap: 'var(--space-2)',
                alignItems: 'stretch'
            }}>
                {['TOFU', 'MOFU', 'BOFU'].map((stage, index) => {
                    const StageIcon = stageIcons[stage];
                    const stageInfo = FUNNEL_STAGES[stage];
                    const stageCampaigns = groupedCampaigns[stage];

                    return (
                        <React.Fragment key={stage}>
                            {/* Stage Column */}
                            <div style={{
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-lg)',
                                padding: 'var(--space-3)',
                                minHeight: '200px',
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                {/* Stage Header */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 'var(--space-3)',
                                    paddingBottom: 'var(--space-2)',
                                    borderBottom: `2px solid ${stageColors[stage]}`
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <div style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: 'var(--radius-md)',
                                            background: `${stageColors[stage]}20`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <StageIcon size={14} style={{ color: stageColors[stage] }} />
                                        </div>
                                        <div>
                                            <div style={{
                                                fontWeight: 600,
                                                fontSize: 'var(--font-size-sm)'
                                            }}>
                                                {stage}
                                            </div>
                                            <div style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-tertiary)'
                                            }}>
                                                {stageInfo.label}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)',
                                        fontWeight: 500
                                    }}>
                                        {formatBudget(stageBudgets[stage])}
                                    </div>
                                </div>

                                {/* Campaign Cards */}
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 'var(--space-2)'
                                }}>
                                    {stageCampaigns.map(campaign => (
                                        <div
                                            key={campaign.id}
                                            style={{
                                                padding: 'var(--space-3)',
                                                background: 'var(--color-surface)',
                                                borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--color-border)',
                                                cursor: 'pointer',
                                                transition: 'all 0.15s ease',
                                                position: 'relative'
                                            }}
                                            onClick={() => onEditCampaign && onEditCampaign(campaign)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.borderColor = stageColors[stage];
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.borderColor = 'var(--color-border)';
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'space-between',
                                                gap: 'var(--space-2)'
                                            }}>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{
                                                        fontWeight: 600,
                                                        fontSize: 'var(--font-size-sm)',
                                                        marginBottom: 'var(--space-1)',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {campaign.name}
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 'var(--space-2)',
                                                        fontSize: 'var(--font-size-xs)',
                                                        color: 'var(--color-text-secondary)'
                                                    }}>
                                                        <span style={{
                                                            padding: '2px 6px',
                                                            background: 'var(--color-bg-tertiary)',
                                                            borderRadius: 'var(--radius-sm)'
                                                        }}>
                                                            {campaign.objective || 'AWARENESS'}
                                                        </span>
                                                        <span style={{ fontWeight: 500 }}>
                                                            {formatBudget(campaign.budget?.amount || 0)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Menu Button */}
                                                <button
                                                    onClick={(e) => handleMenuToggle(campaign.id, e)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        padding: '4px',
                                                        cursor: 'pointer',
                                                        borderRadius: 'var(--radius-sm)',
                                                        color: 'var(--color-text-tertiary)'
                                                    }}
                                                >
                                                    <MoreVertical size={14} />
                                                </button>

                                                {/* Dropdown Menu */}
                                                {openMenuId === campaign.id && (
                                                    <>
                                                        <div
                                                            style={{
                                                                position: 'fixed',
                                                                inset: 0,
                                                                zIndex: 98
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenMenuId(null);
                                                            }}
                                                        />
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '100%',
                                                            right: 0,
                                                            marginTop: 'var(--space-1)',
                                                            background: 'var(--color-surface)',
                                                            border: '1px solid var(--color-border)',
                                                            borderRadius: 'var(--radius-md)',
                                                            boxShadow: 'var(--shadow-lg)',
                                                            zIndex: 99,
                                                            overflow: 'hidden',
                                                            minWidth: '120px'
                                                        }}>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setOpenMenuId(null);
                                                                    onEditCampaign && onEditCampaign(campaign);
                                                                }}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 'var(--space-2)',
                                                                    width: '100%',
                                                                    padding: 'var(--space-2) var(--space-3)',
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    fontSize: 'var(--font-size-sm)',
                                                                    color: 'var(--color-text-primary)'
                                                                }}
                                                                onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-tertiary)'}
                                                                onMouseLeave={(e) => e.target.style.background = 'none'}
                                                            >
                                                                <Edit2 size={14} />
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteClick(campaign);
                                                                }}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 'var(--space-2)',
                                                                    width: '100%',
                                                                    padding: 'var(--space-2) var(--space-3)',
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    fontSize: 'var(--font-size-sm)',
                                                                    color: 'var(--color-error)'
                                                                }}
                                                                onMouseEnter={(e) => e.target.style.background = 'var(--color-bg-tertiary)'}
                                                                onMouseLeave={(e) => e.target.style.background = 'none'}
                                                            >
                                                                <Trash2 size={14} />
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Status indicator */}
                                            <div style={{
                                                position: 'absolute',
                                                top: 'var(--space-2)',
                                                left: 'var(--space-2)',
                                                width: '6px',
                                                height: '6px',
                                                borderRadius: '50%',
                                                background: campaign.status === 'active'
                                                    ? 'var(--color-success)'
                                                    : campaign.status === 'paused'
                                                        ? 'var(--color-warning)'
                                                        : 'var(--color-text-tertiary)'
                                            }} />
                                        </div>
                                    ))}

                                    {/* Add Campaign Button */}
                                    <button
                                        onClick={() => onAddCampaign && onAddCampaign(stage)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-3)',
                                            background: 'transparent',
                                            border: `2px dashed var(--color-border)`,
                                            borderRadius: 'var(--radius-md)',
                                            cursor: 'pointer',
                                            color: 'var(--color-text-secondary)',
                                            fontSize: 'var(--font-size-sm)',
                                            transition: 'all 0.15s ease',
                                            marginTop: stageCampaigns.length === 0 ? 'auto' : 0
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = stageColors[stage];
                                            e.currentTarget.style.color = stageColors[stage];
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = 'var(--color-border)';
                                            e.currentTarget.style.color = 'var(--color-text-secondary)';
                                        }}
                                    >
                                        <Plus size={14} />
                                        Add Campaign
                                    </button>
                                </div>
                            </div>

                            {/* Arrow between stages */}
                            {index < 2 && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--color-text-tertiary)'
                                }}>
                                    <ArrowRight size={20} />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Empty State */}
            {campaigns.length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: 'var(--space-6)',
                    color: 'var(--color-text-secondary)'
                }}>
                    <Target size={32} style={{ marginBottom: 'var(--space-3)', opacity: 0.5 }} />
                    <p style={{ margin: 0 }}>No campaigns yet. Add campaigns to each funnel stage above.</p>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Campaign?"
                message={`Are you sure you want to delete "${campaignToDelete?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />
        </div>
    );
};

export default QuickFunnelBuilder;
